/**
 * Pure helpers for discovering palette colors, themes, and theme variables from
 * the compiled stylesheet. These operate on a minimal {@link StyleRuleLike}
 * abstraction so they can be unit-tested without a real CSSOM (jsdom's support
 * for custom properties on `CSSStyleRule.style` is unreliable).
 *
 * The companion {@link ThemeIntrospectionService} reads the live
 * `document.styleSheets` and converts each `CSSStyleRule` into a
 * `StyleRuleLike` before delegating here.
 */

/** A flattened view of a single CSS style rule's selector + declarations. */
export interface StyleRuleLike {
  selectorText: string;
  declarations: Array<{ name: string; value: string }>;
}

/** A palette color discovered from a `.bg-color-<NAME>` rule. */
export interface PaletteColor {
  name: string;
  hex: string;
}

/**
 * A theme's appearance mode, set via the `--theme-mode` marker. Deliberately
 * lowercase (exempt from the PascalCase literal convention): the values must
 * match the marker strings authored in the theme CSS.
 */
export type ThemeMode = 'light' | 'dark';

/** A theme discovered from a bare single-class rule carrying `--theme-label`. */
export interface ThemeInfo {
  id: string;
  name: string;
  /** Base surface color of the theme; rendered as one half of the picker swatch. */
  mainColor: string;
  /**
   * Signature accent color rendered as the other half of the picker swatch, from
   * the theme's `--theme-swatch` marker. Falls back to {@link mainColor} when the
   * marker is absent (e.g. the neutral light/dark themes), so the swatch reads as
   * a solid circle. The marker exists because dark themes share a near-black
   * background, which would otherwise make their swatches indistinguishable.
   */
  swatchColor: string;
  /**
   * Family id grouping a theme's light/dark variants under one picker entry (from
   * the `--theme-family` marker, e.g. `flamingo`). Falls back to {@link id} when
   * the marker is absent so each theme is at least its own family.
   */
  family: string;
  /**
   * Appearance mode (from the `--theme-mode` marker). Falls back to `light` when
   * the marker is absent.
   */
  mode: ThemeMode;
}

/** A single themed CSS custom property + its authored value. */
export interface ThemeVariable {
  cssVar: string;
  value: string;
}

/** Marker custom property each theme mixin sets; never an editable variable. */
export const THEME_LABEL_VAR = '--theme-label';

/**
 * The original light/dark theme ids (the neutral Magpie family's
 * sunshine/moonshine pair). These always remain available; every other theme is
 * gated behind the additional-themes feature flag.
 */
export const DEFAULT_THEME_IDS: readonly string[] = ['sunshine', 'moonshine'];

/**
 * Optional marker holding the theme's representative swatch color. Metadata, not
 * an editable theme variable.
 */
export const THEME_SWATCH_VAR = '--theme-swatch';

/**
 * Marker grouping a theme's light/dark variants under one picker family (e.g.
 * `--theme-family: 'flamingo'`). Metadata, not an editable theme variable.
 */
export const THEME_FAMILY_VAR = '--theme-family';

/**
 * Marker holding a theme's appearance mode (`--theme-mode: 'light' | 'dark'`).
 * Metadata, not an editable theme variable.
 */
export const THEME_MODE_VAR = '--theme-mode';

/** Marker custom properties that are metadata, never editable theme variables. */
const THEME_MARKER_VARS: ReadonlySet<string> = new Set([
  THEME_LABEL_VAR,
  THEME_SWATCH_VAR,
  THEME_FAMILY_VAR,
  THEME_MODE_VAR,
]);

/**
 * Identification markers for the two shared base rules `_reset.scss` emits ahead
 * of the per-theme delta rules (the full light/dark foundation, emitted once per
 * mode instead of once per theme — see the comment block there).
 *
 * They are inert: never applied to an element, and they lead their rules' selector
 * lists so {@link bareClassOf} files the base layer under the LAYER rather than
 * under whichever family class happens to be listed first.
 */
export const BASE_LIGHT_CLASS = 'theme-base-light';
export const BASE_DARK_CLASS = 'theme-base-dark';

/** The theme's page surface; also the base half of the picker swatch. */
const SURFACE_BACKGROUND_VAR = '--color-surface-background';

/**
 * Every custom property {@link extractThemes} reads — nothing else is needed to
 * build a {@link ThemeInfo}.
 *
 * Exported so `ThemeIntrospectionService` can serve theme discovery from a
 * metadata-only walk of the stylesheet instead of mirroring every declaration in
 * the document. Discovery runs at app bootstrap for every user; the full mirror
 * exists only for the admin-only playground.
 */
export const THEME_METADATA_VARS: readonly string[] = [
  THEME_LABEL_VAR,
  THEME_SWATCH_VAR,
  THEME_FAMILY_VAR,
  THEME_MODE_VAR,
  SURFACE_BACKGROUND_VAR,
];

const BG_COLOR_SELECTOR = /^\.bg-color-(.+)$/;
const BARE_CLASS_SELECTOR = /^\.([a-z0-9_-]+)$/i;

function findDeclaration(rule: StyleRuleLike, name: string): string | undefined {
  return rule.declarations.find(d => d.name === name)?.value;
}

/**
 * The class a rule defines, if any: the bare single-class selector within the
 * rule's (possibly grouped) selector list.
 *
 * Theme rules group their scoped `.theme-light` / `.theme-dark` variant selectors
 * onto the root class rule (see `_reset.scss`), and the two shared base rules
 * group every family class onto themselves — so only the bare class that comes
 * FIRST names the rule. That is why `_reset.scss` keeps the theme class first in
 * a delta rule's list and `.theme-base-{light,dark}` first in a base rule's.
 */
function bareClassOf(selectorText: string): string | undefined {
  for (const part of selectorText.split(',')) {
    const match = part.trim().match(BARE_CLASS_SELECTOR);
    if (match) {
      return match[1];
    }
  }
  return undefined;
}

/** Strip surrounding single/double quotes from a CSS string value. */
export function unquote(value: string): string {
  const trimmed = value.trim();
  if (
    (trimmed.startsWith("'") && trimmed.endsWith("'")) ||
    (trimmed.startsWith('"') && trimmed.endsWith('"'))
  ) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
}

/**
 * Extract the palette from `.bg-color-<NAME>` rules emitted by
 * `generate-color-classes($color-map)`. New palette entries appear here
 * automatically with no code change.
 */
export function extractPalette(rules: StyleRuleLike[]): PaletteColor[] {
  const seen = new Set<string>();
  const palette: PaletteColor[] = [];

  for (const rule of rules) {
    const match = rule.selectorText.match(BG_COLOR_SELECTOR);
    if (!match) {
      continue;
    }
    const name = match[1];
    const hex = findDeclaration(rule, 'background-color');
    if (!hex || seen.has(name)) {
      continue;
    }
    seen.add(name);
    palette.push({ name, hex: hex.trim() });
  }

  return palette;
}

/**
 * Index the sheet's custom properties by the bare class each rule defines, in a
 * single pass.
 *
 * Within a class a re-declared property replaces the earlier one *and takes its
 * position* — the de-duplication engines apply to a declaration block (verified
 * against Chromium: `.a{--x:1;--y:2;--x:3}` enumerates as `--y`, `--x`). Matching
 * that is what lets a composed base + delta map reproduce what a fully expanded
 * theme rule used to yield, value for value AND position for position. Position
 * is observable: the theme playground groups its rows in encounter order.
 */
function indexVarsByClass(rules: StyleRuleLike[]): Map<string, Map<string, string>> {
  const byClass = new Map<string, Map<string, string>>();

  for (const rule of rules) {
    const bare = bareClassOf(rule.selectorText);
    if (!bare) {
      continue;
    }
    let vars: Map<string, string> | undefined;
    for (const { name, value } of rule.declarations) {
      if (!name.startsWith('--')) {
        continue;
      }
      vars ??= byClass.get(bare) ?? new Map<string, string>();
      vars.delete(name);
      vars.set(name, value.trim());
    }
    if (vars) {
      byClass.set(bare, vars);
    }
  }

  return byClass;
}

/**
 * A theme's full variable set: the light/dark base its `--theme-mode` marker
 * selects, with the theme's own delta layered over it — mirroring the two-layer
 * cascade in `_reset.scss` (base rule first, delta second, equal specificity).
 *
 * Falls back to the delta alone when the sheet carries no base layer, so a fully
 * expanded stylesheet still reads correctly. Returns `undefined` when no rule
 * declares custom properties for `themeId`.
 */
function composeTheme(
  byClass: Map<string, Map<string, string>>,
  themeId: string,
): Map<string, string> | undefined {
  const delta = byClass.get(themeId);
  if (!delta) {
    return undefined;
  }
  const mode = unquote(delta.get(THEME_MODE_VAR) ?? '') === 'dark' ? 'dark' : 'light';
  const base = byClass.get(mode === 'dark' ? BASE_DARK_CLASS : BASE_LIGHT_CLASS);
  if (!base) {
    return delta;
  }

  const composed = new Map(base);
  for (const [name, value] of delta) {
    composed.delete(name);
    composed.set(name, value);
  }
  return composed;
}

/**
 * Discover themes from bare single-class rules that carry a `--theme-label`
 * marker (e.g. `.sunshine { --theme-label: 'Sunshine'; ... }`).
 *
 * Metadata is read from the COMPOSED base + delta set, not the theme's own rule:
 * the neutral Magpie delta carries only the markers, so its surface color — the
 * base half of the picker swatch — lives in the base layer.
 */
export function extractThemes(rules: StyleRuleLike[]): ThemeInfo[] {
  const byClass = indexVarsByClass(rules);
  const themes: ThemeInfo[] = [];

  // Iterating the index rather than the rules keeps this a single pass and makes
  // the per-class de-duplication implicit. Map insertion order is first-encounter
  // rule order, so discovery order is unchanged.
  for (const [id, own] of byClass) {
    if (id === BASE_LIGHT_CLASS || id === BASE_DARK_CLASS) {
      continue;
    }
    const label = own.get(THEME_LABEL_VAR);
    if (!label || !label.trim()) {
      continue;
    }
    const mode = unquote(own.get(THEME_MODE_VAR) ?? '') === 'dark' ? 'dark' : 'light';
    const base = byClass.get(mode === 'dark' ? BASE_DARK_CLASS : BASE_LIGHT_CLASS);

    // Read through to the base layer instead of materializing a composed map per
    // theme: only these few values are needed, and this runs at app bootstrap.
    // (The neutral family's delta is markers-only, so its surface color — the base
    // half of the picker swatch — lives in the base layer.)
    const surface = unquote(own.get(SURFACE_BACKGROUND_VAR) ?? base?.get(SURFACE_BACKGROUND_VAR) ?? '');
    const swatch = own.get(THEME_SWATCH_VAR) ?? base?.get(THEME_SWATCH_VAR);
    const family = own.get(THEME_FAMILY_VAR) ?? base?.get(THEME_FAMILY_VAR);
    themes.push({
      id,
      name: unquote(label),
      mainColor: surface,
      swatchColor: swatch ? unquote(swatch) : surface,
      family: family ? unquote(family) : id,
      mode,
    });
  }

  return themes;
}

/**
 * Every editable CSS custom property for a theme: its mode's base layer with the
 * theme's delta composed over it, marker properties stripped, in the declaration
 * order a browser would report.
 */
export function extractThemeVariables(
  rules: StyleRuleLike[],
  themeId: string,
): ThemeVariable[] {
  if (themeId === BASE_LIGHT_CLASS || themeId === BASE_DARK_CLASS) {
    return [];
  }
  const vars = composeTheme(indexVarsByClass(rules), themeId);
  if (!vars) {
    return [];
  }

  const variables: ThemeVariable[] = [];
  for (const [cssVar, value] of vars) {
    if (THEME_MARKER_VARS.has(cssVar)) {
      continue;
    }
    variables.push({ cssVar, value });
  }
  return variables;
}
